import { useEffect } from 'react';

const SITE = 'https://tradesmateuk.co.uk';
const DEFAULT_TITLE = 'TradesMate UK — Trade Calculators & Estimating for UK Tradesmen';

function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr, key, content) {
  if (content == null) return;
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute('content', content);
}

/* Imperative head management — updates the static tags shipped in index.html
   (so crawlers/social scrapers always see correct fallbacks) and keeps exactly
   ONE of each tag. Render exactly one <Seo> per page. */
export default function Seo({ title, description, path = null, image, jsonLd, noindex = false, kicker = '// TradesMate UK' }) {
  const fullTitle = title ? `${title} | TradesMate UK` : DEFAULT_TITLE;
  const url = path != null ? `${SITE}${path}` : SITE;
  const ogTitle = title || 'TradesMate UK — Trade Calculators & Estimating';
  const desc = description || '';
  const img = image || `${SITE}/api/og?title=${encodeURIComponent(ogTitle)}&subtitle=${encodeURIComponent(desc.slice(0, 150))}&kicker=${encodeURIComponent(kicker)}`;
  const ldKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    document.title = fullTitle;

    if (description) setMeta('name', 'description', description);

    // canonical
    const existingCanonical = document.head.querySelector('link[rel="canonical"]');
    if (path != null) {
      const link = existingCanonical || (() => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        document.head.appendChild(l);
        return l;
      })();
      link.setAttribute('href', url);
    } else if (existingCanonical) {
      existingCanonical.remove();
    }

    // robots
    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      setMeta('name', 'robots', 'noindex,nofollow');
    } else if (robots) {
      robots.remove();
    }

    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'TradesMate UK');
    setMeta('property', 'og:title', fullTitle);
    if (description) setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', img);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    if (description) setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', img);

    // JSON-LD structured data
    document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((b) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo-jsonld', '');
        s.textContent = JSON.stringify(b);
        document.head.appendChild(s);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, path, url, img, noindex, ldKey]);

  return null;
}
